import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../firebase/config';
import './Login.css';

function Login() {
  // Auto-fill with mario@google.com as default
  const [email, setEmail] = useState('mario@google.com');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // Update saved email to mario@google.com
  useEffect(() => {
    localStorage.setItem('jookbox_user_email', 'mario@google.com');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      
      // Remember email for next time
      localStorage.setItem('jookbox_user_email', email);
      
      navigate('/library');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Remember email for next time
      if (result.user?.email) {
        localStorage.setItem('jookbox_user_email', result.user.email);
      }
      
      navigate('/library');
    } catch (error) {
      // Don't show error if user closed the popup - this is expected behavior
      // Check both error.code and error.message for different Firebase versions
      const isPopupClosed = error.code === 'auth/popup-closed-by-user' || 
                           error.code === 'auth/cancelled-popup-request' ||
                           error.message?.includes('popup-closed-by-user') ||
                           error.message?.includes('cancelled-popup-request');
      
      if (isPopupClosed) {
        // User closed the popup, silently ignore - this is normal behavior
        // Don't set error state or show any message
        console.log('User closed the authentication popup');
      } else {
        // Show other authentication errors
        console.error('Google sign-in error:', error);
        setError(error.message || 'Failed to sign in with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="container">
        <div className="login-card card">
          <h2 className="login-title">
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </h2>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div style={{ position: 'relative' }}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                required
                autoFocus={!email} // Only focus if email is empty
              />
              {localStorage.getItem('jookbox_user_email') && (
                <span style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.75rem',
                  color: '#1db954',
                  pointerEvents: 'none'
                }}>
                  ✓ Saved
                </span>
              )}
            </div>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
              autoFocus={!!email} // Auto-focus password if email is already filled
            />
            
            <button 
              type="submit" 
              className="button button-primary"
              disabled={loading}
            >
              {loading ? 'Loading...' : (isSignUp ? 'Sign Up' : 'Sign In')}
            </button>
          </form>

          <div className="divider">
            <span>OR</span>
          </div>

          <button 
            onClick={handleGoogleSignIn}
            className="button button-google"
            disabled={loading}
          >
            <span>Continue with Google</span>
          </button>

          <p className="toggle-auth">
            {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
            <button 
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
              }}
              className="toggle-link"
            >
              {isSignUp ? 'Sign In' : 'Sign Up'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;

